import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Smartphone, Brain, Database, Palette, GraduationCap, ArrowRight, CheckCircle, Star, Users, BookOpen, Award, Terminal } from 'lucide-react';
import { getPathway } from '../utils/pathways';

/* ── animated counter ── */
function Counter({ end, suffix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = Math.ceil(end / 60);
        const t = setInterval(() => {
          start = Math.min(start + step, end);
          setVal(start);
          if (start >= end) clearInterval(t);
        }, 20);
        obs.disconnect();
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ── announcement ticker ──
   Edit `tickerItems` to change what scrolls across the banner.
   The track is rendered twice so the CSS keyframe loop is seamless. */
const tickerItems = [
  { text: 'Collaborating with and improving the PaperMinT project.', color: 'var(--ac-purple)' },
  { text: 'Launching soon: Alteracode Academic LMS Web Suite.', color: 'var(--ac-green)' },
];

function Ticker() {
  const row = tickerItems.map((item, i) => (
    <span key={i} className="ac-ticker__item">
      <span className="ac-ticker__dot" style={{ background: item.color, color: item.color }} />
      {item.text}
    </span>
  ));

  return (
    <div className="ac-ticker" role="marquee" aria-label="AlteraCode announcements">
      <div className="ac-ticker__track">
        {row}
        {/* duplicate copy — hidden from screen readers, needed for the seamless loop */}
        <span aria-hidden="true" style={{ display: 'flex' }}>{row}</span>
      </div>
    </div>
  );
}

const services = [
  {
    Icon: Globe,
    slug: 'web-development',
    title: 'Web Development',
    desc: 'Modern, responsive and high-performance web applications built with React, Node.js and the latest web technologies. From landing pages to full enterprise platforms.',
    color: 'var(--ac-cyan)',
    bg: 'rgba(0,200,255,0.08)',
    tags: ['React', 'Node.js', 'MongoDB']
  },
  {
    Icon: Smartphone,
    slug: 'mobile-app-development',
    title: 'Mobile App Development',
    desc: 'Cross-platform mobile applications for Android and iOS using React Native. Beautiful, fast and user-friendly apps that work seamlessly on all devices.',
    color: 'var(--ac-purple)',
    bg: 'rgba(168,85,247,0.08)',
    tags: ['React Native', 'Android', 'iOS']
  },
  {
    Icon: Brain,
    slug: 'ai-machine-learning',
    title: 'AI & Machine Learning',
    desc: 'Custom AI models, machine learning pipelines and intelligent automation solutions. We turn your data into powerful insights and smart applications.',
    color: 'var(--ac-green)',
    bg: 'rgba(0,220,130,0.08)',
    tags: ['Python', 'TensorFlow', 'NLP']
  },
  {
    Icon: Database,
    slug: 'data-engineering',
    title: 'Data Engineering',
    desc: 'Data pipelines, analytics dashboards and database architecture designed for performance and insight. We help you collect, process and visualize your data effectively.',
    color: 'var(--ac-pink)',
    bg: 'rgba(224,64,251,0.08)',
    tags: ['SQL', 'Python', 'Power BI']
  },
  {
    Icon: Palette,
    slug: 'ui-ux-design',
    title: 'UI/UX Design',
    desc: 'Beautiful, intuitive and user-centered designs that make your product stand out. From wireframes to fully polished interfaces — we design experiences people love.',
    color: 'var(--ac-cyan)',
    bg: 'rgba(0,200,255,0.08)',
    tags: ['Figma', 'Prototyping', 'Research']
  },
  {
    Icon: GraduationCap,
    slug: 'altera-scholar-program',
    title: 'Altera Scholar Program',
    desc: 'Our flagship video series for school leavers covering all major IT pathways. Bridging the gap between A/L results and successful tech careers — launching July 2026.',
    color: 'var(--ac-purple)',
    bg: 'rgba(168,85,247,0.08)',
    tags: ['Video Series', 'All IT Paths', 'July 2026']
  },
];

/* `id` is the pathway track — icon, accent colour and badge style all come
   from utils/pathways.js so every page shows the same mark for a pathway. */
const courses = [
  { id: 'computer-science', track: 'Computer Science',      title: 'Computer Science Pathway',       dur: 'Self-paced', lessons: 52 },
  { id: 'data-science',     track: 'Data Science',          title: 'Data Science Pathway',           dur: 'Self-paced', lessons: '--' },
  { id: 'ai-ml',            track: 'Artificial Intelligence', title: 'AI & Machine Learning Pathway', dur: 'Self-paced', lessons: '--' },
];

const testimonials = [
  { name: '', role: 'UI/UX Designer', text: 'In AlteraCode I found the best environment to grow my skills. The team is very supportive and the projects are challenging and rewarding.', stars: 5 },
  { name: '', role: 'AI Engineer', text: 'The hands-on projects and mentorship made all the difference. I highly recommend .', stars: 5 },
  { name: '', role: 'Backend Developer', text: 'The best investment I made before entering the tech industry. Real projects, real results.', stars: 5 },
];

// Products array for Rolling News
const rollingProducts = [
  {
    title: 'Alt Lumina',
    subtitle: 'Academic LMS Suite',
    desc: 'The complete automation suite for schools and tuition institutes: classes, exams, fees and parents in one platform.',
    image: '/images/alt_lumina_logo.jpg',
    color: 'var(--ac-purple)',
    link: '/services#alt-lumina'
  },
  {
    title: 'AlteraCart',
    subtitle: 'E-Commerce Platform',
    desc: 'A complete online store for Sri Lankan businesses, from product page to paid order, with local payment gateways.',
    image: '/images/alteracart_logo.jpg',
    color: 'var(--ac-cyan)',
    link: '/services#alteracart'
  },
  {
    title: 'AltAttendance Pro',
    subtitle: 'Attendance & Progress',
    desc: 'Scan students in, enter marks once, and share progress online. Keeps the classroom physical and records online.',
    image: '/images/altattendance_logo.jpg',
    color: 'var(--ac-green)',
    link: '/services#altattendance-pro'
  },
  {
    title: 'Breaking Every Protocol',
    subtitle: 'AlteraCode Media Unit',
    desc: <>Our latest tech rap anthem. From the classroom to the codebase... we don't just learn, we deploy. <Terminal size={14} style={{ display: 'inline', verticalAlign: 'text-bottom' }} /></>,
    image: 'https://img.youtube.com/vi/CJveXpkl4-A/maxresdefault.jpg',
    color: 'var(--ac-pink)',
    link: '/services#music-library',
    badgeText: 'OUT NOW'
  }
];

function RollingNews() {
  const row = rollingProducts.map((p, i) => (
    <div key={i} className="card" style={{
      width: 340,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      marginRight: '1.5rem',
      padding: '1.25rem'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="badge" style={{ background: p.color + '15', color: p.color, border: `1px solid ${p.color}40`, fontSize: 10 }}>
          {p.badgeText || 'LAUNCHING SOON'}
        </div>
      </div>
      <div style={{ height: 160, borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--ac-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src={p.image} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '1rem' }} />
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ac-white)', marginBottom: 4 }}>{p.title}</h3>
        <p style={{ fontSize: 12, color: p.color, fontWeight: 600, marginBottom: 8 }}>{p.subtitle}</p>
        <p style={{ fontSize: 13, color: 'var(--ac-muted)', lineHeight: 1.6 }}>{p.desc}</p>
      </div>
      <Link to={p.link} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: p.color, fontSize: 13, fontWeight: 600, textDecoration: 'none', marginTop: '0.5rem' }}>
        Learn more <ArrowRight size={14} />
      </Link>
    </div>
  ));

  return (
    <section className="section-sm" style={{ background: 'var(--ac-dark)', borderBottom: '1px solid var(--ac-border)' }}>
      <div className="container" style={{ marginBottom: '2.5rem', textAlign: 'center' }}>
        <div className="badge badge-cyan" style={{ marginBottom: '1rem' }}>Pre-Orders Open</div>
        <h2 style={{ fontSize: 32, fontWeight: 800, color: 'var(--ac-white)' }}>
          Latest Platforms <span className="grad-text">Launching Soon</span>
        </h2>
      </div>
      <div className="ac-rolling-container">
        <div className="ac-rolling-track">
          {row}
          {row}
          {row}
          {row}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [hoveredService, setHoveredService] = useState(null);
  const [hoveredCourse, setHoveredCourse] = useState(null);

  return (
    <div style={{ paddingTop: 68 }}>

      {/* ── HERO ── */}
      <section style={{
        minHeight: '92vh', display: 'flex', alignItems: 'center',
        background: `
          radial-gradient(ellipse at 20% 50%, rgba(0,200,255,0.07) 0%, transparent 55%),
          radial-gradient(ellipse at 80% 20%, rgba(168,85,247,0.07) 0%, transparent 55%),
          var(--ac-dark)`,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.03,
          backgroundImage: 'linear-gradient(rgba(0,200,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,200,255,1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }} />
        <div className="container" style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <div className="badge badge-purple" style={{ fontSize: 12 }}>
              Now Accepting First Projects
            </div>
          </div>
          <h1 style={{ fontSize: 'clamp(36px, 6vw, 72px)', fontWeight: 800, color: 'var(--ac-white)', lineHeight: 1.1, marginBottom: '1.5rem' }}>
            Building the Future with<br />
            <span className="grad-text">Code That Transforms</span>
          </h1>
          <p style={{ fontSize: 18, color: 'var(--ac-muted)', maxWidth: 620, margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
            AlteraCode is a rising global IT company powered by 13+ passionate developers — building innovative real-world technology solutions and empowering the next generation to launch their tech careers through Altera Scholar.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: '4rem' }}>
            <Link to="/services" className="btn btn-primary btn-lg">Explore Services <ArrowRight size={18} /></Link>
            <Link to="/educenter" className="btn btn-edu btn-lg">Altera Scholar</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Get a Quote</Link>
          </div>
          {/* Stats */}
          <div style={{ display: 'flex', gap: '3rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {[
              { end: 13, suffix: '+', label: 'Expert Developers' },
              { end: 8, suffix: '+', label: 'IT Pathways' },
              { end: 2026, suffix: '', label: 'Founded' },
              { end: 100, suffix: '%', label: 'Remote Ready' },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--ac-white)' }}>
                  <Counter end={s.end} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: 13, color: 'var(--ac-muted)', marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ANNOUNCEMENT TICKER ── */}
      <Ticker />

      {/* ── SERVICES ── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="badge badge-cyan" style={{ marginBottom: '1rem' }}>What We Do</div>
            <h2>Our Core Services</h2>
            <p>Real-world technology solutions across cybersecurity, artificial intelligence, and software development.</p>
          </div>
          <div className="grid-3">
            {services.map((s, i) => (
              /* Each card deep-links to its matching section on the Services page */
              <Link key={i} to={`/services#${s.slug}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
              <div className="card"
                onMouseEnter={() => setHoveredService(i)}
                onMouseLeave={() => setHoveredService(null)}
                style={{
                  cursor: 'pointer',
                  height: '100%',
                  transform: hoveredService === i ? 'translateY(-6px)' : 'none',
                  borderColor: hoveredService === i ? s.color + '55' : 'var(--ac-border)',
                  boxShadow: hoveredService === i ? `0 12px 40px ${s.color}15` : 'none',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                {/* Icon */}
                <div style={{
                  width: 48, height: 48, borderRadius: 'var(--radius-md)',
                  background: s.bg, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', marginBottom: '1rem',
                  border: `1px solid ${s.color}25`,
                  transition: 'transform 0.3s',
                  transform: hoveredService === i ? 'scale(1.1)' : 'scale(1)',
                }}>
                  <s.Icon size={22} color={s.color} />
                </div>

                {/* Title */}
                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--ac-white)', marginBottom: 8 }}>
                  {s.title}
                </h3>

                {/* Description */}
                <p style={{ fontSize: 13, color: 'var(--ac-muted)', lineHeight: 1.6, flex: 1 }}>
                  {s.desc}
                </p>

                {/* Tags */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: '1rem' }}>
                  {s.tags.map((tag, ti) => (
                    <span key={ti} style={{
                      fontSize: 10, padding: '3px 8px',
                      background: s.color + '12',
                      color: s.color,
                      borderRadius: 20,
                      border: `1px solid ${s.color}25`,
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    }}>{tag}</span>
                  ))}
                </div>

                {/* Hover arrow */}
                <div style={{
                  marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: 6,
                  color: s.color, fontSize: 13, fontWeight: 500,
                  opacity: hoveredService === i ? 1 : 0,
                  transform: hoveredService === i ? 'translateX(0)' : 'translateX(-8px)',
                  transition: 'all 0.3s',
                }}>
                  Learn more <ArrowRight size={14} />
                </div>
              </div>
              </Link>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Link to="/services" className="btn btn-outline">View All Services <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* ── ROLLING NEWS ── */}
      <RollingNews />

      <div className="glow-divider" />

      {/* ── EDUCENTER HIGHLIGHT ── */}
      <section className="section" style={{
        background: `
          radial-gradient(ellipse at 80% 50%, rgba(168,85,247,0.06) 0%, transparent 60%),
          radial-gradient(ellipse at 20% 50%, rgba(0,200,255,0.04) 0%, transparent 60%)`,
      }}>
        <div className="container">
          <div className="layout-2col-even">
            <div>
              <div className="badge badge-purple" style={{ marginBottom: '1.2rem' }}><GraduationCap size={14} /> AlteraCode EduCenter</div>
              <h2 style={{ fontSize: 38, fontWeight: 800, color: 'var(--ac-white)', lineHeight: 1.2, marginBottom: '1rem' }}>
                <span className="grad-text-edu">Altera Scholar</span><br />Bridge the A/L to Career Gap
              </h2>
              <p style={{ fontSize: 15, color: 'var(--ac-muted)', lineHeight: 1.8, marginBottom: '1.5rem' }}>
                Altera Scholar is our flagship video series program designed for school leavers. We cover every major IT pathway — from Computer Science to Data Science — helping students bridge the gap between A/L results and a successful tech career. No university required to get started.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: '2rem' }}>
                {[
                  'Video series covering all major IT pathways',
                  'Designed specifically for A/L school leavers',
                  'Bridges the gap between A/L and IT careers',
                  'Mentorship from AlteraCode\'s own developers',
                  'Real-world projects and portfolio building',
                  'Certificate of completion for each pathway',
                  'Guidance for university and career decisions'
                ].map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--ac-text)' }}>
                    <CheckCircle size={16} color="var(--ac-green)" />
                    {f}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <Link to="/educenter" className="btn btn-edu">Browse Courses</Link>
                {/* Enrolments are closed — points at the pathway list instead of registration */}
                <Link to="/educenter" className="btn btn-outline-purple">Explore Pathways →</Link>
              </div>
            </div>
            {/* Course preview cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {courses.map((c, i) => {
                const p = getPathway(c.id);
                return (
                <Link key={i} to={`/educenter?track=${c.id}`} style={{ textDecoration: 'none' }}>
                  <div className="card-edu"
                    onMouseEnter={() => setHoveredCourse(i)}
                    onMouseLeave={() => setHoveredCourse(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '1rem',
                      transform: hoveredCourse === i ? 'translateX(6px)' : 'none',
                    }}>
                    <div className="pathway-tile" style={{
                      width: 52, height: 52,
                      background: p.color + '14',
                      border: `1px solid ${p.color}30`,
                      transform: hoveredCourse === i ? 'scale(1.08)' : 'scale(1)',
                    }}>
                      <p.Icon size={24} color={p.color} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className={`badge ${p.badge}`} style={{ marginBottom: 4, fontSize: 10 }}>{c.track}</div>
                      <h4 style={{ fontSize: 15, fontWeight: 700, color: 'var(--ac-white)' }}>{c.title}</h4>
                      <p style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{c.dur} · {c.lessons} video lessons</p>
                    </div>
                    <ArrowRight size={18} color={p.color} style={{ opacity: hoveredCourse === i ? 1 : 0.3, transition: 'opacity 0.2s' }} />
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <div className="glow-divider" />

      {/* ── WHY ALTERACODE ── */}
      <section className="section-sm" style={{ background: 'var(--ac-navy)' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {[
              { Icon: BookOpen, val: '100%', label: 'Online & flexible', color: 'var(--ac-cyan)' },
              { Icon: Users, val: '---', label: 'Students enrolled', color: 'var(--ac-purple)' },
              { Icon: Award, val: '3', label: 'Career tracks', color: 'var(--ac-green)' },
              { Icon: Star, val: '--', label: 'Average rating', color: 'var(--ac-pink)' },
            ].map(({ Icon, val, label, color }, i) => (
              <div key={i} style={{ flex: 1, minWidth: 180, background: 'var(--ac-card)', border: '1px solid var(--ac-border)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <div style={{ width: 44, height: 44, borderRadius: 'var(--radius-md)', background: color + '12', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={20} color={color} />
                </div>
                <div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--ac-white)' }}>{val}</div>
                  <div style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <div className="badge badge-green" style={{ marginBottom: '1rem' }}>Student Stories</div>
            <h2>What Our Students Say</h2>
          </div>
          <div className="grid-3">
            {testimonials.map((t, i) => (
              <div key={i} className="card" style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 3, marginBottom: '1rem' }}>
                  {[...Array(t.stars)].map((_, j) => <Star key={j} size={14} fill="var(--ac-warn)" color="var(--ac-warn)" />)}
                </div>
                <p style={{ fontSize: 14, color: 'var(--ac-text)', lineHeight: 1.7, marginBottom: '1.2rem', fontStyle: 'italic' }}>"{t.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'var(--grad-edu)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: 'white' }}>
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ac-white)' }}>{t.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section-sm">
        <div className="container">
          <div style={{
            background: 'linear-gradient(135deg, rgba(0,200,255,0.08), rgba(168,85,247,0.08))',
            border: '1px solid rgba(0,200,255,0.2)', borderRadius: 'var(--radius-xl)',
            padding: '3.5rem', textAlign: 'center',
          }}>
            <h2 style={{ fontSize: 36, fontWeight: 800, color: 'var(--ac-white)', marginBottom: '0.75rem' }}>
              Be Our <span className="grad-text">First Partners</span>
            </h2>
            <p style={{ fontSize: 16, color: 'var(--ac-muted)', marginBottom: '2rem', maxWidth: 520, margin: '0 auto 2rem', lineHeight: 1.8 }}>
              We are a fresh startup with 13+ skilled developers ready to take on exciting projects.
              Be among the first to work with AlteraCode and get dedicated attention, competitive rates
              and a team that truly cares about your success.
            </p>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn btn-primary btn-lg">Work With Us</Link>
              <Link to="/educenter" className="btn btn-edu btn-lg">Start Learning</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
